<template>
  <div>
    <empty-state v-if="loading" :title="$t('loading')" :loading="true" />

    <empty-state
      v-else-if="!visibleCompanies.length"
      icon="mdi-food-off"
      :title="$t('no_data')"
      :subtitle="$t('no_meals_for_selected_period')"
    />

    <v-card
      v-else
      v-for="company in visibleCompanies"
      :key="company.id || company.name"
      flat
      outlined
      class="company-card mt-6"
    >
      <v-card-title class="justify-center text-center">
        <div>
          <h2 class="company-title">{{ company.name }}</h2>
          <div class="company-details mt-2">
            <span v-if="company.address">
              <v-icon small>mdi-map-marker-outline</v-icon> {{ company.address }}
            </span>
            <span v-if="company.city">
              <v-icon small>mdi-city-variant-outline</v-icon> {{ company.city }}
            </span>
            <span v-if="company.phone">
              <v-icon small>mdi-phone-outline</v-icon> {{ company.phone }}
            </span>
            <v-chip v-for="source in company.sources" :key="source" x-small>
              {{ $t(source === "more_restaurants" ? "more_restaurants" : "meal_in_meals") }}
            </v-chip>
          </div>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="getMobileHeaders(headers)"
          :items="customSearch(company.items, headers, filters.search, ['combination'])"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
        >
          <template v-slot:item.name="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">{{ item.name }}</span>
              </template>
              <span>{{ item.id }}</span>
            </v-tooltip>
            <div v-if="item.combination" class="text-caption grey--text">
              {{ item.combination }}
            </div>
          </template>
          <template v-for="shift in selectedShiftColumns" v-slot:[`item.${shift.key}`]="{ item }">
            <span :key="shift.key">{{ formatQuantity(item, shift.key) }}</span>
          </template>
          <template v-slot:item.total="{ item }">
            {{ formatQuantity(item, "total") }}
          </template>
          <template #body.append>
            <tr class="font-weight-bold">
              <td v-if="!device.isMobile"></td>
              <td>{{ $t("total") }}</td>
              <td v-for="shift in selectedShiftColumns" :key="shift.key" class="text-right">
                {{ formatQuantity(company.totals, shift.key) }}
              </td>
              <td class="text-right">{{ formatQuantity(company.totals, "total") }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import reportService from "@/services/report";
import EmptyState from "@/components/global/EmptyState.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS } from "@/constants/shifts";

/**
 * "Sve firme – zbirni izvještaj", kartica "Pakovanje": po firmi stavke iz
 * orders i orders_more_restaurants, "naručeno / preuzeto" po smjeni.
 */
export default {
  name: "ReportAllCompaniesTabPacking",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: { EmptyState },
  props: {
    selectedShifts: {
      type: Array,
      default: () => SHIFTS.map((shift) => shift.id),
    },
  },
  data: () => ({
    loading: false,
    companies: [],
  }),
  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    selectedShiftColumns() {
      return SHIFTS.filter((shift) => this.selectedShifts.includes(shift.id));
    },
    /** Ukupno samo za izabrane smjene; bez stavki i firmi koje tada ostaju prazne. */
    visibleCompanies() {
      const recount = (row) => {
        const visible = { ...row, total: 0, total_payed: 0 };
        SHIFTS.forEach(({ id, key }) => {
          const isSelected = this.selectedShifts.includes(id);
          visible[key] = isSelected ? row[key] : 0;
          visible[`${key}_payed`] = isSelected ? row[`${key}_payed`] : 0;
          visible.total += visible[key];
          visible.total_payed += visible[`${key}_payed`];
        });
        return visible;
      };

      return this.companies
        .map((company) => ({
          ...company,
          items: company.items
            .map(recount)
            .filter((item) => item.total > 0)
            .map((item, index) => ({ ...item, num: index + 1 })),
          totals: recount(company.totals),
        }))
        .filter((company) => company.items.length);
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
        ...this.selectedShiftColumns.map((shift) => ({
          text: this.$t(shift.label),
          value: shift.key,
          align: "right",
        })),
        { text: this.$t("total"), value: "total", align: "right" },
      ];
    },
  },
  methods: {
    async load() {
      if (!this.restaurantId || !this.filters.dates.from) {
        return;
      }
      this.loading = true;
      try {
        const { data } = await reportService.getAllCompaniesPacking(this.restaurantId, {
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        });
        this.companies = (Array.isArray(data) ? data : []).map((company) => ({
          ...company,
          name: this.toLatin(company.name || ""),
          address: company.address ? this.toLatin(company.address) : null,
          city: company.city ? this.toLatin(company.city) : null,
        }));
      } catch (error) {
        this.companies = [];
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    /** Excel: naziv sheet-a najviše 31 znak, bez : \\ / ? * [ ] i jedinstven. */
    sheetName(name, index) {
      const suffix = ` ${index + 1}`;
      return name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31 - suffix.length).trim() + suffix;
    },
    formatQuantity(row, key) {
      return `${row[key] || 0} / ${row[`${key}_payed`] || 0}`;
    },
    /** Tabela firme za štampu/Excel: "naručeno / preuzeto", kombinacija uz naziv paketa. */
    exportTable(company) {
      const quantityKeys = [...this.selectedShiftColumns.map((shift) => shift.key), "total"];
      const toExportRow = (row) => ({
        num: row.num ?? "",
        name: row.combination ? `${row.name} (${row.combination})` : row.name,
        ...Object.fromEntries(quantityKeys.map((key) => [key, this.formatQuantity(row, key)])),
      });

      return [
        ...this.customSearch(company.items, this.headers, this.filters.search, ["combination"]).map(
          toExportRow
        ),
        toExportRow({ ...company.totals, num: "", name: this.$t("totalU") }),
      ];
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
    },
    restaurantId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    "$store.state.report.reload"() {
      this.load();
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          tables: this.visibleCompanies.map((company) => ({
            headers: this.headers,
            items: this.exportTable(company),
            title: `${company.name} - ${this.subtitle}`,
            subtitle: [this.$t("all_companies_summary_report"), this.$t("pak")].join(": "),
          })),
          hideDatetime: true,
          additionalStyles: `
            .print-table { page-break-after: always; }
            .print-table:last-child { page-break-after: auto; }
          `,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("all_companies_summary_report"), this.$t("pak")].join(" ") + ".xlsx",
          sheets: this.visibleCompanies.map((company, index) => ({
            name: this.sheetName(company.name, index),
            headers: this.headers,
            items: this.exportTable(company),
            title: company.name,
            subtitle: this.subtitle,
          })),
        });
      }
    },
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.company-title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
}

.company-details {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  color: #546e7a;
  font-size: 0.95rem;
  font-weight: 400;
}
</style>
